import { useCallback, useMemo, useState } from 'react';
import { BudgetScenarioWorktypeOption } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import {
    DraftIssue,
    DraftUpdater,
    ScenarioDriverDraftRow,
    driverRowHasInput,
    driverTrainingCost,
    patchDraftMonthValue,
    patchTrainingRowField,
    removeDraftRow,
} from './scenarioDraft';
import { formatCurrency, formatDecimal, SCENARIO_ENTITY_COL_MIN_WIDTH } from './helpers';
import ScenarioRowGrid, { ScenarioRowColumn, ScenarioRowOption } from './ScenarioRowGrid';

interface ScenarioDriverGridProps {
    months: string[];
    /** Read for RENDERING only: an edit is derived from the `previous` an updater is handed, never from this
     *  prop (see DraftUpdater). */
    rows: ScenarioDriverDraftRow[];
    onChange: (update: DraftUpdater<ScenarioDriverDraftRow[]>) => void;
    issues: DraftIssue[];
    availableWorktypes: BudgetScenarioWorktypeOption[];
    isLoading: boolean;
    canEdit: boolean;
    /** National public-holiday dates ('YYYY-MM-DD') the Trainingstage prefill subtracts besides weekends
     *  (CALL-801) — the same set the backend's training allocation uses. Undefined while the page is still
     *  loading them, which suppresses the prefill rather than counting holidays as working days. */
    holidays?: ReadonlySet<string>;
}

// Input grid for a GA/CB/VVL ("Erreichbarkeit Anpassung") scenario: one row per worktype with its editable
// assumptions (WL per 1000, cost-per-contact), training inputs and a monthly business-driver delta.
// `Trainingskosten` is derived, not entered. Inputs only — the calculated cost impact (driver € + one-time
// training cost) is the section's other view.
//
// Everything mechanical lives in ScenarioRowGrid (CALL-783); this file owns the column list, the per-row
// worktype choices and the edit handlers. One row per worktype: each row's dropdown excludes worktypes
// already used by other rows, and draftIssues enforces the same rule for the save.
//
// There is no add-row button: the rows ARE the form's worktype multiselect (the server has a
// UNIQUE (scenarioId, worktypeId) index, so the two are a bijection), and the trash writes back into that
// selection. Edits report a new draft upward; the page's single Save commits them.
const ScenarioDriverGrid = ({
    months,
    rows,
    onChange,
    issues,
    availableWorktypes,
    isLoading,
    canEdit,
    holidays,
}: ScenarioDriverGridProps) => {
    const { t } = useTranslation('budget');
    // Removing a row that holds values discards them on the next save — confirm first. A still-blank row
    // can go without friction.
    const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

    // Worktypes already taken by other rows — excluded from each row's picker so a worktype is used at
    // most once (also enforced server-side by the unique index).
    const usedWorktypeIds = useMemo(
        () => rows.map((row) => row.worktypeId).filter((id): id is number => id !== ''),
        [rows]
    );

    const worktypeOptionsFor = useCallback(
        (row: ScenarioDriverDraftRow): ScenarioRowOption[] => {
            const options = availableWorktypes
                .filter(
                    (option) => option.worktypeId === row.worktypeId || !usedWorktypeIds.includes(option.worktypeId)
                )
                .map((option) => ({ id: option.worktypeId, label: option.worktypeName }));
            // A persisted worktype no longer offered by the bucket (e.g. after the scenario is rebased or a
            // billing config is removed) still renders with its saved label rather than a blank,
            // out-of-range value.
            if (row.worktypeId !== '' && !options.some((option) => option.id === row.worktypeId)) {
                options.push({ id: row.worktypeId, label: row.worktypeName ?? `#${row.worktypeId}` });
            }
            return options;
        },
        [availableWorktypes, usedWorktypeIds]
    );

    // Board order (screen_029): the worktype, its training block with the derived total, then the driver
    // assumptions, then the monthly change. Only the worktype column is wide (SCENARIO_ENTITY_COL_MIN_WIDTH,
    // the width its impact-grid counterpart pins at); the rest hold 2-5 digits, as on the board.
    //
    // Sized for the CELLS since CALL-784 — a euro figure needs ~80px, a count ~40 — rather than for the
    // captions, which now truncate with a tooltip. That took the leading block from 1360 to 805px, so the
    // month block starts well inside a 1400px viewport instead of being bisected by the pinned trash.
    const columns = useMemo<ScenarioRowColumn<ScenarioDriverDraftRow>[]>(
        () => [
            {
                kind: 'select',
                key: 'worktypeId',
                // The board's literal header is "Titel"; the cell holds a worktype, so it says so.
                headerName: t('field.worktype'),
                width: SCENARIO_ENTITY_COL_MIN_WIDTH,
                pinned: 'left',
                options: worktypeOptionsFor,
            },
            // The training window (CALL-801), exactly like the Verschiebung grid's pair — 78 is the
            // smallest width that shows a full German date, see ScenarioShiftGrid for the arithmetic.
            // Unlike there, Start may stay empty: the cost is then spread from the scenario start (and a
            // new row is prefilled with that date, so the fallback is visible rather than implicit).
            { kind: 'date', key: 'trainingStart', headerName: t('field.trainingStart'), width: 78 },
            { kind: 'date', key: 'trainingEnd', headerName: t('field.trainingEnd'), width: 78 },
            {
                kind: 'number',
                key: 'headsTraining',
                headerName: t('field.headsTraining'),
                width: 95,
                format: formatDecimal,
            },
            {
                kind: 'number',
                key: 'trainingDays',
                headerName: t('field.trainingDays'),
                width: 92,
                format: formatDecimal,
            },
            {
                kind: 'number',
                key: 'costPerHour',
                headerName: t('field.costPerHour'),
                width: 70,
                format: formatCurrency,
            },
            {
                kind: 'number',
                key: 'trainerResource',
                headerName: t('field.trainerResource'),
                width: 90,
                format: formatCurrency,
            },
            {
                // Six-figure euro total (€89.664 on the board) — the one numeric column that needs the room.
                kind: 'computed',
                field: 'trainingTotal',
                headerName: t('field.trainingTotal'),
                width: 92,
                compute: driverTrainingCost,
                format: formatCurrency,
            },
            {
                kind: 'number',
                key: 'wlPer1000',
                headerName: t('field.wlPer1000'),
                // The one caption CALL-784 lengthened to the board's own ("WL/1000 CB/GA", which says which
                // driver the workload is per 1000 of), so it gets the room to read in full.
                width: 105,
                format: formatDecimal,
            },
            { kind: 'number', key: 'cpc', headerName: t('field.cpc'), width: 85, format: formatCurrency },
        ],
        [t, worktypeOptionsFor]
    );

    // Every handler below patches the rows it is HANDED, never the `rows` prop: one gesture (Ctrl+D down the
    // WL/1000 column, a pasted block of driver deltas) fires several cellValueChanged events in a single
    // macrotask with no render between them, so a patch built from the prop would be built from the pre-burst
    // state and only the last would survive (see DraftUpdater in scenarioDraft).
    const onFieldChange = useCallback(
        (clientKey: string, key: keyof ScenarioDriverDraftRow & string, value: number | string) => {
            // patchTrainingRowField (CALL-801): Start/Ende prefill Trainingstage exactly as on the
            // Verschiebung grid; for every other key it degrades to the plain single-field patch.
            onChange((previous) => patchTrainingRowField(previous, clientKey, key, value, holidays));
        },
        [onChange, holidays]
    );

    const onRemoveRow = useCallback(
        (row: ScenarioDriverDraftRow) => {
            if (!driverRowHasInput(row)) {
                onChange((previous) => removeDraftRow(previous, row.clientKey));
                return;
            }
            setDeleteConfirmKey(row.clientKey);
        },
        [onChange]
    );

    const onConfirmDelete = useCallback(() => {
        if (deleteConfirmKey === null) return;
        onChange((previous) => removeDraftRow(previous, deleteConfirmKey));
        setDeleteConfirmKey(null);
    }, [onChange, deleteConfirmKey]);

    const rowLabel = useCallback(
        (row: ScenarioDriverDraftRow) =>
            row.worktypeId === ''
                ? ''
                : (worktypeOptionsFor(row).find((option) => option.id === row.worktypeId)?.label ?? ''),
        [worktypeOptionsFor]
    );

    return (
        <ScenarioRowGrid<ScenarioDriverDraftRow>
            months={months}
            rows={rows}
            columns={columns}
            isLoading={isLoading}
            canEdit={canEdit}
            issues={issues}
            formatMonthValue={formatDecimal}
            rowLabel={rowLabel}
            onFieldChange={onFieldChange}
            onMonthValueChange={(clientKey, month, value) =>
                onChange((previous) => patchDraftMonthValue(previous, clientKey, month, value))
            }
            onRemoveRow={onRemoveRow}
            deleteConfirmOpen={deleteConfirmKey !== null}
            onCancelDelete={() => setDeleteConfirmKey(null)}
            onConfirmDelete={onConfirmDelete}
            labels={{
                noRows: t('scenarioDetail.noDriverRows'),
                deleteRow: t('scenarioDetail.deleteDriverRow'),
            }}
        />
    );
};

export default ScenarioDriverGrid;
